import { observer } from 'mobx-react-lite';
import React from 'react';
import { ApPushChoice, ApPushSwitch } from 'src/components/ApPushSwitch';
import { BillReadyDayField } from 'src/components/BillReadyDayField';
import { isScraperSupported, propertyUtilityIdOf } from 'src/components/property/UtilityPortalCredentials/credentialStatus';
import { UtilitySetupSection } from 'src/components/UtilitySetupSection';
import { SetupOnSaveNote } from 'src/components/UtilitySetupSection/ownership';
import { useStore } from 'src/stores';

/**
 * The top of a utility's details on the setup page.
 *
 * With billing options off, as before: AP Push for a saved utility, the bill ready day while editing, and the AP Push
 * choice for one not saved yet (LB-6031). Fetched utilities only get the switch: an unsaved provider option's id is a
 * utility-type id, not a property utility.
 *
 * With them on, the utility's setup takes AP Push's place (LB-6186): AP Push is the setup's now. The bill ready day
 * stays, since it's the utility's own. A utility not saved yet links to a setup when it is, so it only says so.
 *
 * `propertyId` and `provider` (the page's provider row) let the setup show the utility's portal login (LB-6191). The
 * row's id is the utility provider's, as the page's template modal sends it.
 */
export const UtilityDetailsHeader = observer(_ref => {
  let utility = _ref.utility,
    isEdit = _ref.isEdit,
    propertyId = _ref.propertyId,
    provider = _ref.provider,
    onLinkRead = _ref.onLinkRead,
    onLinked = _ref.onLinked,
    onSavingChange = _ref.onSavingChange,
    disabled = _ref.disabled;
  const store = useStore();
  const propertyUtilityId = propertyUtilityIdOf(utility);
  if (!store.account.featureFlagsStore.getIsBillingOptionsEnabled) {
    return utility.isFetched ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ApPushSwitch, {
      propertyUtilityId: propertyUtilityId
    }), isEdit ? /*#__PURE__*/React.createElement(BillReadyDayField, {
      propertyUtilityId: propertyUtilityId
    }) : null) : /*#__PURE__*/React.createElement(ApPushChoice, {
      checked: utility.isApPushEnabled,
      onChange: value => store.addNewProperty.utilitiesSetupV2.setApPushChoice(utility, value)
    });
  }
  if (!utility.isFetched) return /*#__PURE__*/React.createElement(SetupOnSaveNote, {
    typeName: utility.name
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(UtilitySetupSection, {
    propertyUtilityId: propertyUtilityId,
    utilityTypeId: utility.utilityTypeId,
    propertyId: propertyId,
    provider: provider ? {
      utilityProviderId: provider.id,
      name: provider.name,
      isFetchable: isScraperSupported(provider)
    } : undefined,
    onLinkRead: onLinkRead,
    onLinked: onLinked,
    onSavingChange: onSavingChange,
    disabled: disabled
  }), isEdit ? /*#__PURE__*/React.createElement(BillReadyDayField, {
    propertyUtilityId: propertyUtilityId
  }) : null);
});